import { z } from "zod";

import type { StageBranding } from "./branding.ts";

/**
 * Customer View — Verträge zwischen API und Web.
 *
 * Alle Ticket-DTOs entstehen serverseitig durch einen fail-closed
 * Allowlist-Mapper (`sanitizer.service.ts`), der den Rohvorgang nie spreadet:
 * Worklog, Zeiten, Budgets und Salesforce-Felder kommen hier per Konstruktion
 * nicht vor. Bei Ids und Adressen ist genau zu unterscheiden:
 *
 * - **Listen-DTO** (`customerSafeTicketSchema`, `customerSafeCommentSchema`):
 *   trägt **keine** Account-Ids und **keine** E-Mail-Adressen, von Personen
 *   nur Anzeigenamen. Das ist der DTO, der bühnentauglich ist.
 * - **Detail-DTO** (`customerSafeTicketDetailSchema`): trägt zusätzlich
 *   `assignee.accountId`, weil der Bearbeiter-Editor der Scheibe 3 die Id für
 *   den Schreibaufruf braucht. Damit ist das Detail **nur fürs Cockpit des
 *   Leads** — es gehört nie auf die Bühne.
 */
/**
 * Die Jira-Site, gegen die die Customer View für diesen Lead arbeitet. Sie
 * folgt dem OAuth-Token, nicht `JIRA_BASE_URL`: Der Lead wählt sie beim
 * Zustimmen bei Atlassian. Deshalb gehört sie sichtbar in die Oberfläche —
 * auf Staging kann ein Lead sonst unbemerkt in echten Prod-Tickets arbeiten.
 */
export const customerViewSiteSchema = z.object({
  /** Host der Site, z. B. `mindsquare-service.atlassian.net`. */
  host: z.string(),
  cloudId: z.string(),
});
export type CustomerViewSite = z.infer<typeof customerViewSiteSchema>;

export const customerViewConnectionSchema = z.object({
  /** Ist das Modul serverseitig konfiguriert? Steuert den Navigationseintrag. */
  configured: z.boolean(),
  /** Hat dieser Lead eine gespeicherte Jira-Verbindung? */
  connected: z.boolean(),
  jiraAccountId: z.string().nullable(),
  /** `null`, solange nicht verbunden oder die Site nicht bestimmbar ist. */
  site: customerViewSiteSchema.nullable().default(null),
  /**
   * Warum die Site nicht bestimmbar ist, wenn der Lead das durch Neu-Verbinden
   * lösen kann (Token gilt für keine oder für mehrere Sites). Sonst `null` —
   * ein Ausfall bei Atlassian ist kein Auftrag an den Lead.
   */
  siteProblem: z.string().nullable().default(null),
  /**
   * Derselbe Fall maschinenlesbar, damit die Oberfläche den Satz in der
   * Sprache des Nutzers bildet statt die deutsche Servermeldung anzuzeigen.
   * `SITE_AMBIGUOUS` = mehrere Sites ohne Treffer, `SITE_NONE` = das Token
   * gilt für keine, `SITE_UNKNOWN` = die Site war gerade nicht bestimmbar
   * (Ausfall bei Atlassian — **kein** Auftrag an den Lead, deshalb ohne
   * Reconnect-Banner). `null` heißt: kein bekannter Fall — dann zählt der
   * Text aus `siteProblem`.
   */
  siteProblemCode: z.enum(["SITE_AMBIGUOUS", "SITE_NONE", "SITE_UNKNOWN"]).nullable().default(null),
  /**
   * Host aus `JIRA_BASE_URL` — die Umgebung, in der dieses CRM läuft. Damit
   * lässt sich erkennen, ob die Site des Tokens von ihr abweicht (Staging
   * gegen Prod): der riskante Fall, in dem ein Token die falsche Umgebung
   * trifft. `null`, wenn das Modul nicht konfiguriert ist.
   */
  configuredHost: z.string().nullable().default(null),
});
export type CustomerViewConnection = z.infer<typeof customerViewConnectionSchema>;

/** `mine` = der Lead ist Service Lead der Kundenabteilung (Assets-Attribut 618). */
export const customerFilterSchema = z.enum(["mine", "all"]);
export type CustomerFilter = z.infer<typeof customerFilterSchema>;

/** Eine Kundenabteilung aus Jira Assets (Objekttyp „Kundenabteilung"). */
export const customerObjectSchema = z.object({
  objectKey: z.string(),
  name: z.string(),
  isMine: z.boolean(),
  bereich: z.object({ key: z.string(), name: z.string() }).nullable(),
});
export type CustomerObject = z.infer<typeof customerObjectSchema>;

export const customersResponseSchema = z.object({ customers: z.array(customerObjectSchema) });

export const customerSafeCommentSchema = z.object({
  id: z.string(),
  /** Nur der Anzeigename — nie E-Mail, nie Account-Id. */
  authorName: z.string(),
  createdIso: z.string(),
  bodyText: z.string(),
});
export type CustomerSafeComment = z.infer<typeof customerSafeCommentSchema>;

export const customerSafeAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
});
export type CustomerSafeAttachment = z.infer<typeof customerSafeAttachmentSchema>;

const idNameSchema = z.object({ id: z.string(), name: z.string() });

/**
 * Jira-Label der Steering-Tickets („Projektleitung / Steering – …", #336). Sie
 * laufen im Kundentermin standardmäßig nicht mit: In der Vorbereitung sind sie
 * abgewählt, der Lead kann sie für den Termin wieder anwählen. Dauerhaft
 * entscheidet das Label in Jira — eine Stelle, kein zweiter Schalter im CRM.
 */
export const STEERING_LABEL = "Projektleitung";

/** Listenzeile — nur öffentliche Kommentare. */
export const customerSafeTicketSchema = z.object({
  key: z.string(),
  title: z.string(),
  status: z.string(),
  /** Jira-Kategorie: new | indeterminate | done — stabil über Sprachen und Workflows. */
  statusCategory: z.string(),
  issueType: z.string(),
  descriptionText: z.string().nullable(),
  createdIso: z.string(),
  updatedIso: z.string(),
  assigneeName: z.string().nullable(),
  reporterName: z.string().nullable(),
  priority: idNameSchema.nullable(),
  publicComments: z.array(customerSafeCommentSchema),
  attachments: z.array(customerSafeAttachmentSchema),
  /**
   * Trägt das Label `STEERING_LABEL` (#336). Nur die Ja/Nein-Ableitung reist
   * mit, nicht die Labels selbst — der Mapper bleibt fail-closed.
   */
  steering: z.boolean().optional(),
});
export type CustomerSafeTicket = z.infer<typeof customerSafeTicketSchema>;

export const ticketsResponseSchema = z.object({ tickets: z.array(customerSafeTicketSchema) });

/** Kommentar im Detail — gerendertes, sanitisiertes HTML, mit Sichtbarkeits-Flag. */
export const customerSafeRichCommentSchema = z.object({
  id: z.string(),
  authorName: z.string(),
  createdIso: z.string(),
  bodyHtml: z.string(),
  /** false = interner Kommentar; die Oberfläche blendet ihn per Schalter ein. */
  isPublic: z.boolean(),
});
export type CustomerSafeRichComment = z.infer<typeof customerSafeRichCommentSchema>;

/** Detail — alle Kommentare (mit Flag), Beschreibung als HTML, Optionen für Scheibe 3. */
export const customerSafeTicketDetailSchema = z.object({
  key: z.string(),
  title: z.string(),
  status: z.string(),
  issueType: z.string(),
  createdIso: z.string(),
  updatedIso: z.string(),
  assigneeName: z.string().nullable(),
  /** YYYY-MM-DD */
  dueDate: z.string().nullable(),
  browseUrl: z.string(),
  descriptionHtml: z.string(),
  assignee: z.object({ accountId: z.string(), displayName: z.string() }).nullable(),
  priority: idNameSchema.nullable(),
  requestType: idNameSchema.nullable(),
  priorityOptions: z.array(idNameSchema),
  requestTypeOptions: z.array(idNameSchema),
  comments: z.array(customerSafeRichCommentSchema),
  attachments: z.array(customerSafeAttachmentSchema),
});
export type CustomerSafeTicketDetail = z.infer<typeof customerSafeTicketDetailSchema>;

// ---------------------------------------------------------------------------
// Schreibpfad (Scheibe 3, Spec D8): Übergänge, Kommentar, Felder, Formulare
// ---------------------------------------------------------------------------

/** ISO-Datum ohne Zeit, wie Jiras `duedate`. */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const numericIdSchema = z.string().regex(/^\d+$/);
/** Jira-Schlüssel-Format (Objekt- wie Ticket-Schlüssel): Projekt-Präfix + Zahl, z. B. `MS-1`. */
const jiraKeySchema = z.string().regex(/^[A-Z]+-\d+$/);

/**
 * Feldarten der Übergangsmaske, die das Cockpit selbst darstellt (#335).
 * Abgeleitet aus `schema` und `allowedValues` der Jira-Antwort
 * (`expand=transitions.fields`). Was keiner Art entspricht (Assets-Objekte,
 * Arbeitszeit, Personen …), kann nur Jira — s. `jiraOnlyRequired`.
 */
export const transitionFieldKindSchema = z.enum(["select", "multiselect", "date", "number", "text", "textarea"]);
export type TransitionFieldKind = z.infer<typeof transitionFieldKindSchema>;

export const transitionFieldSchema = z.object({
  /** Jira-Feldkennung („resolution", „duedate", „customfield_11197"). */
  key: z.string().min(1).max(100),
  /** Anzeigename aus Jira („Lösung"). */
  name: z.string(),
  kind: transitionFieldKindSchema,
  required: z.boolean(),
  allowedValues: z.array(idNameSchema).optional(),
});
export type TransitionField = z.infer<typeof transitionFieldSchema>;

/** Wert eines Maskenfelds: Auswahl-Id, Ids, Text/Datum (YYYY-MM-DD), Zahl; `null` leert. */
export const transitionFieldValueSchema = z.union([
  z.string().max(5000),
  z.array(z.string().max(100)).max(50),
  z.number(),
  z.null(),
]);
export type TransitionFieldValue = z.infer<typeof transitionFieldValueSchema>;

/** Ein vom Cockpit angebotener Übergang; `name` ist der Transition-Name („Resolve"), `toStatus` der Zielstatus („Erledigt"). */
export const transitionOptionSchema = z.object({
  id: numericIdSchema,
  name: z.string(),
  toStatus: z.string(),
  toStatusCategory: z.enum(["new", "indeterminate", "done"]),
  fields: z.array(transitionFieldSchema),
  /**
   * Pflichtfelder, die das Cockpit nicht darstellen kann (#335). Ist die Liste
   * nicht leer, bietet das Cockpit den Übergang nicht zum Absenden an, sondern
   * verweist auf Jira — ein Absenden, das sicher scheitert, gibt es nicht.
   */
  jiraOnlyRequired: z.array(z.object({ key: z.string(), name: z.string() })),
  /** Ziel-Kategorie `done`: Jira lehnt ab, solange ein angehängtes Formular nicht eingereicht ist. */
  requiresSubmittedForms: z.boolean(),
});
export type TransitionOption = z.infer<typeof transitionOptionSchema>;

export const ticketFormSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  internal: z.boolean(),
  submitted: z.boolean(),
  locked: z.boolean(),
});
export type TicketFormSummary = z.infer<typeof ticketFormSummarySchema>;

export const ticketActionsResponseSchema = z.object({
  transitions: z.array(transitionOptionSchema),
  forms: z.array(ticketFormSummarySchema),
});
export type TicketActionsResponse = z.infer<typeof ticketActionsResponseSchema>;

export const commentVisibilitySchema = z.enum(["public", "internal"]);
export type CommentVisibility = z.infer<typeof commentVisibilitySchema>;

/**
 * Eine @-Erwähnung im Kommentar (#275): Wer im Text als „@Anzeigename" steht,
 * wird in Jira als echte Erwähnung angelegt — Jira benachrichtigt die Person.
 * Die Account-Id kommt aus der Personensuche des Tickets; ohne passenden
 * Text im Kommentar bleibt der Eintrag wirkungslos.
 */
export const commentMentionSchema = z.object({
  accountId: z.string().min(1).max(128),
  displayName: z.string().trim().min(1).max(200),
});
export type CommentMention = z.infer<typeof commentMentionSchema>;

export const commentRequestSchema = z.object({
  text: z.string().trim().min(1).max(20_000),
  visibility: commentVisibilitySchema,
  mentions: z.array(commentMentionSchema).max(20).default([]),
});
/** Was ein Aufrufer schickt — `mentions` darf fehlen (Vorgabe `[]`). */
export type CommentRequest = z.input<typeof commentRequestSchema>;

export const commentCreatedSchema = z.object({ commentId: z.string() });

export const transitionRequestSchema = z.object({
  transitionId: numericIdSchema,
  /** Werte der Übergangsmaske je Feldkennung (#335). Nur Felder dieses Übergangs. */
  fields: z.record(z.string().min(1).max(100), transitionFieldValueSchema).optional(),
  /** Kurzform für `fields.resolution` (älter, bleibt für MCP-Aufrufer). */
  resolutionId: numericIdSchema.optional(),
  /** Kurzform für `fields.duedate`; `null` leert. */
  dueDate: isoDateSchema.nullable().optional(),
  comment: commentRequestSchema.optional(),
});
export type TransitionRequest = z.input<typeof transitionRequestSchema>;

/** Antwort von `POST /tickets/:key/transition`. */
export const transitionAppliedSchema = z.object({ ok: z.literal(true), toStatus: z.string() });
export type TransitionApplied = z.infer<typeof transitionAppliedSchema>;

/** Antwort der übrigen Schreibrouten ohne eigene Nutzlast (Kommentar, Fälligkeit, Priorität, Bearbeiter). */
export const writeOkSchema = z.object({ ok: z.literal(true) });
export type WriteOk = z.infer<typeof writeOkSchema>;

export const dueDateRequestSchema = z.object({ dueDate: isoDateSchema.nullable() });
export const priorityRequestSchema = z.object({ priorityId: numericIdSchema });
export const assigneeRequestSchema = z.object({ accountId: z.string().min(1).nullable() });

export const assignableUserSchema = z.object({ accountId: z.string(), displayName: z.string() });
export type AssignableUser = z.infer<typeof assignableUserSchema>;
export const assignableUsersResponseSchema = z.object({ users: z.array(assignableUserSchema) });

/**
 * Neues Ticket im Termin anlegen (#271), wie im Kundenportal: Anfragetyp
 * wählen, dann dessen Felder. Wählbar sind die Typen, die das Portal zeigt
 * (mit Portalgruppe); die Gruppen bleiben als Überschriften erhalten.
 */
export const requestTypeSchema = z.object({
  id: numericIdSchema,
  name: z.string(),
  description: z.string(),
  /** Portalgruppen in Portalreihenfolge; ein Typ kann in mehreren stehen. */
  groups: z.array(z.string()),
});
export type RequestType = z.infer<typeof requestTypeSchema>;
export const requestTypesResponseSchema = z.object({ requestTypes: z.array(requestTypeSchema) });
export type RequestTypesResponse = z.infer<typeof requestTypesResponseSchema>;

/** Maske eines Anfragetyps — dieselben Feldarten wie bei den Übergängen (#335). */
export const requestTypeFormSchema = z.object({
  fields: z.array(transitionFieldSchema),
  /** Pflichtfelder, die das Cockpit nicht kann; ist die Liste nicht leer, geht dieser Typ nur in Jira. */
  jiraOnlyRequired: z.array(z.object({ key: z.string(), name: z.string() })),
  /** Darf der Lead für einen Kundenkontakt anlegen? Sonst ist er selbst der Melder. */
  canRaiseOnBehalfOf: z.boolean(),
});
export type RequestTypeForm = z.infer<typeof requestTypeFormSchema>;

/**
 * Ein Kundenkontakt, der in den Tickets dieses Kunden schon gemeldet hat.
 * Jira leitet Kundenfirma und Kundenabteilung eines neuen Tickets aus dem
 * Melder ab (die Felder stehen nicht in der Anlagemaske) — deshalb wird für
 * einen Kontakt angelegt, nicht unter dem eigenen Namen.
 */
export const customerContactSchema = z.object({ accountId: z.string(), displayName: z.string() });
export type CustomerContact = z.infer<typeof customerContactSchema>;
export const customerContactsResponseSchema = z.object({ contacts: z.array(customerContactSchema) });

export const ticketCreateRequestSchema = z.object({
  requestTypeId: numericIdSchema,
  fields: z.record(z.string().min(1).max(100), transitionFieldValueSchema),
  /** Account-Id des Kundenkontakts; ohne Angabe meldet der Lead selbst. */
  raiseOnBehalfOf: z.string().min(1).max(128).optional(),
});
export type TicketCreateRequest = z.infer<typeof ticketCreateRequestSchema>;

/**
 * Antwort auf das Anlegen. `customerDepartmentSet: false` heißt: Jira hat dem
 * neuen Ticket (noch) nicht die Kundenabteilung dieses Kunden gegeben — dann
 * erscheint es nicht im Deck, und das Cockpit sagt das, statt still zu warten.
 */
export const ticketCreatedSchema = z.object({ key: jiraKeySchema, customerDepartmentSet: z.boolean() });
export type TicketCreated = z.infer<typeof ticketCreatedSchema>;

/** Fragetyp der Forms-API (`cm` Checkboxen, `cs` Radio, `cd` Dropdown, `ts`/`tl` Text, sonst nur lesbar). */
export const formQuestionSchema = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string(),
  description: z.string().optional(),
  required: z.boolean(),
  choices: z.array(z.object({ id: z.string(), label: z.string() })),
});
export type FormQuestion = z.infer<typeof formQuestionSchema>;

export const formAnswerSchema = z.object({
  text: z.string().optional(),
  choices: z.array(z.string()).optional(),
});
export type FormAnswer = z.infer<typeof formAnswerSchema>;

export const ticketFormSchema = ticketFormSummarySchema.extend({
  questions: z.array(formQuestionSchema),
  answers: z.record(formAnswerSchema),
});
export type TicketForm = z.infer<typeof ticketFormSchema>;

export const ticketFormsResponseSchema = z.object({ forms: z.array(ticketFormSchema) });

export const formSubmitRequestSchema = z.object({ answers: z.record(formAnswerSchema) });
export type FormSubmitRequest = z.infer<typeof formSubmitRequestSchema>;

/** Antwort von `PUT /tickets/:key/forms/:formId`; `status` folgt dem Rückgabetyp von `FormsService.saveAndSubmit`. */
export const formSubmittedSchema = z.object({ ok: z.literal(true), status: z.enum(["open", "submitted", "locked"]) });
export type FormSubmitted = z.infer<typeof formSubmittedSchema>;

// ---------------------------------------------------------------------------
// Präsentationsmodus (Scheibe 4): Zusammenfassungen, Protokoll, Events, Bühne
// ---------------------------------------------------------------------------

const issueKeysSchema = z.array(jiraKeySchema).min(1).max(50);

export const ticketSummaryDtoSchema = z.object({
  issueKey: z.string(),
  text: z.string(),
  /** Vom Lead redigiert; hat Vorrang bis zur nächsten Ticket-Änderung. */
  edited: z.boolean(),
  /** Kein LLM-Text (Fehler, Ablehnung, nicht konfiguriert): letzter öffentlicher Kommentar oder Beschreibung. Nicht gecacht. */
  fallback: z.boolean(),
});
export type TicketSummaryDto = z.infer<typeof ticketSummaryDtoSchema>;

export const summariesRequestSchema = z.object({ customerObjectKey: jiraKeySchema, issueKeys: issueKeysSchema });
export type SummariesRequest = z.infer<typeof summariesRequestSchema>;
/**
 * `summaries` steht in der Reihenfolge der angefragten `issueKeys` (Deck- =
 * Folienreihenfolge), nicht in der von Jira. `missingKeys` nennt die
 * Schlüssel, zu denen es beim Kunden kein Ticket gab: Die Anfrage scheitert
 * daran nicht — im Termin ist eine fehlende Folie besser als ein Fehler —,
 * aber sie verschwinden auch nicht still.
 */
export const summariesResponseSchema = z.object({
  summaries: z.array(ticketSummaryDtoSchema),
  missingKeys: z.array(z.string()),
});
export type SummariesResponse = z.infer<typeof summariesResponseSchema>;

/**
 * Redaktion durch den Lead — auch ohne bestehenden Cache-Eintrag (Bearbeitung
 * eines Rückfalls, s. `SummaryStore.setEdited`) und auch, wenn das Ticket sich
 * seither weiterbewegt hat: `issueUpdatedAt` ist die Ticket-Version, für die
 * die Redaktion gilt — der Server speichert sie mit, damit die Bearbeitung
 * bei einer erneuten Ticket-Änderung (anderes `updatedIso`) korrekt verworfen
 * wird statt fälschlich fortzubestehen.
 */
export const summaryEditRequestSchema = z.object({
  text: z.string().trim().min(1).max(600),
  // Obergrenze, weil der Wert unverändert in den Audit-Payload wandert: Jiras
  // `updated` ist ein ISO-Zeitstempel mit Offset (~28 Zeichen), 40 lässt Luft
  // für Schreibweisen und schneidet alles ab, was kein Zeitstempel mehr ist.
  issueUpdatedAt: z.string().min(1).max(40),
});

// ---------------------------------------------------------------------------
// Agenda-Folien (D9)
// ---------------------------------------------------------------------------
export const agendaKindSchema = z.enum(["neuigkeiten", "wartung", "budget"]);
export type AgendaKind = z.infer<typeof agendaKindSchema>;
export const AGENDA_KINDS: readonly AgendaKind[] = ["neuigkeiten", "wartung", "budget"];
export const budgetDisplaySchema = z.enum(["euro", "restprozent"]);
export type BudgetDisplay = z.infer<typeof budgetDisplaySchema>;

/**
 * Längengrenzen für Agenda-Titel und -Text — einmal definiert, damit API-Schema
 * und Web-Formulare (Zeichenzähler, `maxLength`) nie auseinanderlaufen.
 */
/** Titel einer Agenda-Vorlage oder -Folie (`agendaTemplateSchema`, `agendaItemSchema`). */
export const AGENDA_TITLE_MAX = 120;
/**
 * Gerenderter Markdown-Text einer Agenda-**Folie** (nach Platzhalter-Ersetzung),
 * nicht die Vorlage — die trägt `AGENDA_TEMPLATE_BODY_MAX`. Eine Wartungs- oder
 * Budgettabelle mit vielen Zeilen (bis zu `AGENDA_TABLE_MAX_ROWS` in
 * `agendaPlaceholders.ts`) sprengt `AGENDA_TEMPLATE_BODY_MAX` schnell; dieser
 * Wert reicht selbst für den gedeckelten Tabellenumfang mit Luft.
 */
export const AGENDA_ITEM_BODY_MAX = 20_000;
/** Freitext einer Agenda-**Vorlage** (`agendaTemplateSchema`, `agendaTemplateUpdateSchema`). */
export const AGENDA_TEMPLATE_BODY_MAX = 5000;

export const agendaTemplateSchema = z.object({
  kind: agendaKindSchema,
  title: z.string().min(1).max(AGENDA_TITLE_MAX),
  body: z.string().max(AGENDA_TEMPLATE_BODY_MAX),
  budgetDisplay: budgetDisplaySchema,
  segmentId: z.string().uuid().nullable(),
  /** true = Zeile ist ein Override des Bereichs, false = Standard greift. */
  isOverride: z.boolean(),
});
export type AgendaTemplate = z.infer<typeof agendaTemplateSchema>;
export const agendaTemplatesResponseSchema = z.object({ segmentId: z.string().uuid().nullable(), templates: z.array(agendaTemplateSchema) });
export const agendaTemplateUpdateSchema = z.object({
  segmentId: z.string().uuid().nullable(),
  kind: agendaKindSchema,
  title: z.string().trim().min(1).max(AGENDA_TITLE_MAX),
  body: z.string().max(AGENDA_TEMPLATE_BODY_MAX),
  budgetDisplay: budgetDisplaySchema,
});
export type AgendaTemplateUpdate = z.infer<typeof agendaTemplateUpdateSchema>;

export const agendaItemSchema = z.object({
  kind: agendaKindSchema,
  /**
   * `.trim()` vor `min(1)`: Ein Titel aus lauter Leerzeichen ist keiner — er
   * stünde als leere Überschrift im Ergebnisprotokoll, das der Kunde behält.
   * Dieselbe Reihenfolge wie in `agendaTemplateUpdateSchema`.
   */
  title: z.string().trim().min(1).max(AGENDA_TITLE_MAX),
  /**
   * Diese Schema-Instanz wird an zwei Stellen wiederverwendet
   * (`agendaResponseSchema.items`, `protocolRequestSchema.agendaItems`) —
   * beide profitieren von einer Änderung an `AGENDA_ITEM_BODY_MAX` ohne
   * separate Anpassung.
   */
  body: z.string().max(AGENDA_ITEM_BODY_MAX),
  enabled: z.boolean(),
});
export type AgendaItem = z.infer<typeof agendaItemSchema>;
export const agendaResponseSchema = z.object({
  /** false = die Agenda kommt OHNE CRM-Tabellen; `reason` sagt, warum. */
  linked: z.boolean(),
  /**
   * Warum keine CRM-Zahlen auf der Folie stehen — `null`, wenn sie dort stehen.
   *
   * - `unlinked`: keine Beauftragung trägt diese Kundenabteilung
   *   (`engagements.jira_asset_id`); es gibt schlicht nichts zu zeigen.
   * - `out_of_reach`: es gibt eine Verknüpfung, aber ihr Bereich liegt
   *   außerhalb der Reichweite des Leads. Dann bleiben `segment` und
   *   `engagements` gefüllt — der Termin ist durchführbar, nur Budget und
   *   Wartungsstände fehlen (Grenze nach demselben Muster wie die Kennzahlen:
   *   Gerüst `segmentReach.ts`, die erreichbare Menge entscheidet das Modul).
   */
  reason: z.enum(["unlinked", "out_of_reach"]).nullable().default(null),
  segment: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  engagements: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  items: z.array(agendaItemSchema),
});
export type AgendaResponse = z.infer<typeof agendaResponseSchema>;

export const protocolRequestSchema = z.object({
  customerObjectKey: jiraKeySchema,
  customerName: z.string().trim().min(1).max(200),
  issueKeys: issueKeysSchema,
  reviewedKeys: z.array(z.string()).max(100),
  agendaItems: z.array(agendaItemSchema).max(10).default([]),
});
export type ProtocolRequest = z.infer<typeof protocolRequestSchema>;
export const protocolResponseSchema = z.object({ markdown: z.string(), fazitGenerated: z.boolean() });
export type ProtocolResponse = z.infer<typeof protocolResponseSchema>;

export const upcomingEventSchema = z.object({
  title: z.string(),
  type: z.string(),
  dateIso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  description: z.string(),
  url: z.string(),
});
export type UpcomingEvent = z.infer<typeof upcomingEventSchema>;
export const eventsResponseSchema = z.object({ events: z.array(upcomingEventSchema) });

// Bühne: reine Typen. Sie kreuzen keine HTTP-Grenze, nur den BroadcastChannel
// zwischen Cockpit und Bühnenfenster desselben Browsers.
export const STAGE_CHANNEL = "msc-customer-view-stage";
export type StageMode = "agenda" | "agendaItem" | "ticket" | "pause" | "end";
/** `customer` = Design des Kunden (D10); die Bühne setzt die Farben dann aus `StageSnapshot.branding`. */
export type StageTheme = "dark" | "light" | "customer";
export interface StageDeckItem {
  key: string;
  title: string;
  reviewed: boolean;
  /**
   * Gruppe aus der Ticketliste, eingefroren beim Start der Vorbereitung
   * (#278) — Status, Typ oder Ersteller, alles Felder, die auf der Bühne
   * ohnehin stehen. Optional: ungruppierte Liste, Nachzügler, älteres Cockpit.
   */
  group?: string;
}
/** Ein Kommentar auf der Bühne; `internal: true` NUR nach expliziter Einzelfreigabe im Cockpit. */
export interface StageComment { id: string; authorName: string; createdIso: string; bodyHtml: string; internal: boolean }
/** Eine Agenda-Folie auf der Bühne; `bodyHtml` ist im Cockpit aus Markdown gerendert und wird auf der Bühne nochmals durch `sanitizeForDisplay` geführt. */
export interface StageAgendaItem { kind: AgendaKind; title: string; bodyHtml: string }
export interface StageSnapshot {
  mode: StageMode;
  theme: StageTheme;
  customerName: string;
  deck: StageDeckItem[];
  /** Index ins Deck; -1 bei agenda/end. */
  currentIndex: number;
  agendaItems: StageAgendaItem[];
  /** Index in `agendaItems`; -1 außerhalb des Modus `agendaItem`. */
  agendaIndex: number;
  current: {
    ticket: CustomerSafeTicket;
    /**
     * Beschreibung des Vorgangs als HTML: aus dem Detail-Cache (serverseitig
     * `sanitize-html`, Bilder über den Attachment-Proxy), ersatzweise der
     * escapte Klartext des Tickets. Die Bühne führt sie nochmals durch
     * `sanitizeForDisplay`. Leer, wenn der Vorgang keine Beschreibung hat.
     */
    descriptionHtml: string;
    /** KI-Zusammenfassung („Aktueller Stand"); leer, wenn keine erzeugt wurde. */
    summaryText: string;
    dueDate: string | null;
    comments: StageComment[];
    shownAttachment: { id: string; filename: string } | null;
    /**
     * Id eines Kommentars aus `comments`, den der Lead gerade groß zeigt
     * (#272). Optional, weil ein älteres Cockpit es noch nicht schickt.
     * Zeigt nie auf etwas außerhalb von `comments` — `buildStageSnapshot`
     * setzt es nur dann, sonst würde ein nicht freigegebener interner
     * Kommentar über den Fokus auf die Bühne geraten.
     */
    focusedCommentId?: string | null;
  } | null;
  stats: { total: number; done: number } | null;
  events: UpcomingEvent[] | null;
  /**
   * Kundendesign (D10): Logo-Adresse und zwei Farben. Optional, weil ein
   * älteres Cockpit es noch nicht schickt — die Bühne bleibt dann bei
   * dark/light (Spec D5: nie brechen).
   */
  branding?: StageBranding | null;
}
/**
 * Jede Nachricht trägt den `owner`: die Kennung der Präsentation, zu der sie
 * gehört (`ownerId` im Präsentations-Store, vom Cockpit beim Start vergeben
 * und dem Kundenfenster in der Adresse mitgegeben). Der BroadcastChannel ist
 * je Origin — JEDER offene CRM-Tab hört mit, und ein zweiter Tab mit einer
 * wiederhergestellten Präsentation antwortete dem Kundenfenster sonst mit
 * seinem Stand (2026-09-23: ein Ticket als erste Folie statt der Agenda).
 */
export type PresentMessage =
  | { type: "state"; owner: string; snapshot: StageSnapshot }
  | { type: "state-request"; owner: string }
  | { type: "ready"; owner: string }
  | { type: "closed"; owner: string }
  /**
   * Bühne → Cockpit: Weiter/Zurück, gedrückt im Kundenfenster (#267). Mit
   * nur einem Bildschirm liegt der Fokus dort, nicht im Cockpit. Die Bühne
   * leitet nur weiter — blättern (samt Auto-Haken) tut weiter das Cockpit,
   * die Bühne bleibt ohne eigenen Zustand.
   */
  | { type: "nav"; owner: string; action: StageNavAction };

export type StageNavAction = "next" | "prev";
